<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { onBeforeRouteLeave } from 'vue-router'

import HomePageFooter from '../components/HomePageFooter.vue'
import HomePageHeader from '../components/HomePageHeader.vue'
import LandingBackground from '../components/LandingBackground.vue'
import MessageDialog from '../components/MessageDialog.vue'
import TaskSubmitProgressDialog from '../components/TaskSubmitProgressDialog.vue'
import SegmentedSingleSelect from '../workbench/SegmentedSingleSelect.vue'
import TaskStatusTag from '../components/TaskStatusTag.vue'
import IconChevronLeft from '../components/icons/IconChevronLeft.vue'
import IconChevronRight from '../components/icons/IconChevronRight.vue'
import IconDownload from '../components/icons/IconDownload.vue'
import IconPlay from '../components/icons/IconPlay.vue'
import IconRefresh from '../components/icons/IconRefresh.vue'
import IconClose from '../components/icons/IconClose.vue'
import IconTrash from '../components/icons/IconTrash.vue'
import { apiRequest, assertOk, isAuthRequiredError, AUTH_EXPIRED_EVENT, AUTH_EXPIRED_STORAGE_KEY } from '../lib/apiClient'
import { brand } from '../config/brand'
import type { LandingSectionKey } from '../lib/landingTypes'
import { absoluteUrl, organizationJsonLd, webApplicationJsonLd } from '../lib/structuredData'
import { getVideoUpscaleCreditsStandardText, getVideoUpscaleHintText } from '../lib/taskCredits'
import { getTaskThumbFromCache, removeTaskThumbCache, setTaskThumbToCache } from '../lib/taskThumbCache'
import { TASK_STATUS, normalizeTaskStatus } from '../lib/taskStatus'
import { getUploadUrl, putFile } from '../lib/upload'
import { TASK_TYPES } from '../workbench/taskCatalog'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'
import { usePaymentModalStore } from '../stores/paymentModal'
import { useTaskStore } from '../stores/task'
import { useVideoTaskListStore } from '../stores/videoTasks'

type LandingRecommendation = { icon: string; name: string; desc: string; href: string }
type StartTaskResponse = { task_id?: string; status?: string; credits_required?: number }
type VideoUpscaleResponse = { task_id?: string; task_name?: string; status?: string; credits_required?: number }

type UpscaleOption = 'UPS_1080P' | 'UPS_2K'

const TASK_TYPE = TASK_TYPES.upscale_vid

const props = withDefaults(
  defineProps<{
    landingPageId?: string
    landingSeo?: any
    canonicalPath?: string
    breadcrumbName?: string
    howToTitle?: string
    seoTitle?: string
    seoDescription?: string
    benefitsImages?: string[]
    featuresImages?: string[]
    whyChooseImages?: string[]
    enabledSections?: Partial<Record<LandingSectionKey, boolean>>
    embed?: boolean
    demoVideoUrl?: string
    defaultInvitationCode?: string
    recommendations?: LandingRecommendation[]
    hreflang?: Record<string, string>
  }>(),
  {
    landingPageId: '',
    landingSeo: undefined,
    canonicalPath: '/video-enhance-app/',
    breadcrumbName: '视频转高清',
    howToTitle: undefined,
    seoTitle: '',
    seoDescription: '',
    benefitsImages: () => [],
    featuresImages: () => [],
    whyChooseImages: () => [],
    enabledSections: () => ({}),
    embed: false,
    demoVideoUrl: 'https://7375-suyoo-4gfus2th28bc15ad-1327486388.cos.ap-shanghai.myqcloud.com/demo/video-upscale-demo-1080P.mp4',
    defaultInvitationCode: 'OF3A7K',
    recommendations: () => [],
    hreflang: () => ({}),
  },
)

const auth = useAuthStore()
const taskStore = useTaskStore()
const videoTasks = useVideoTaskListStore()
const loginModal = useLoginModalStore()
const paymentModal = usePaymentModalStore()

const siteUrl = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  'https://www.wuhenai.com'
).replace(/\/+$/, '')

function runtimeBasePath() {
  const v = (import.meta.env.BASE_URL as string | undefined) || '/'
  return v === '/' ? '/' : v.replace(/\/?$/, '/')
}
function withBase(p: string) {
  const s = String(p || '').trim()
  if (!s) return ''
  if (/^https?:\/\//.test(s)) return s
  return `${runtimeBasePath()}${s.replace(/^\//, '')}`
}

function defaultVideoThumbDataUrl() {
  return withBase('/assets/default-video-thumb.svg')
}

async function createThumb(): Promise<string> {
  const vid = videoEl.value
  if (!vid) return ''
  const c = document.createElement('canvas')
  const w = Math.max(1, vid.videoWidth || videoW.value || 1)
  const h = Math.max(1, vid.videoHeight || videoH.value || 1)
  const scale = Math.min(1, 320 / w)
  c.width = Math.round(w * scale)
  c.height = Math.round(h * scale)
  const ctx = c.getContext('2d')
  if (!ctx) return ''
  ctx.drawImage(vid, 0, 0, c.width, c.height)
  return c.toDataURL('image/jpeg', 0.72)
}

function taskThumbSrc(t: any) {
  const id = String(t?.task_id || '').trim()
  const cached = getTaskThumbFromCache(id)
  if (cached) return cached
  const remote = String(t?.task_thumb || '').trim()
  if (remote) return remote
  const local = String(t?.thumb_data_url || '').trim()
  if (local && local.startsWith('data:image/')) {
    setTaskThumbToCache(id, local)
    return local
  }
  return ''
}

const tutorialIconMaskStyle = computed(() => {
  const href = withBase('/assets/tutorial-icon.svg')
  return { backgroundColor: 'currentColor', WebkitMask: `url("${href}") no-repeat center / contain`, mask: `url("${href}") no-repeat center / contain` } as any
})

function openHowTo() {
  window.open(
    'https://suiyu-network.feishu.cn/wiki/FtpvwylhQiqWNAklbeMcWls1n7b?larkTabName=space#share-AUwidOJDcohqWMxZxoHc7d4MnIh',
    '_blank',
    'noopener,noreferrer',
  )
}

const sectionEnabled = (key: LandingSectionKey) => props.enabledSections?.[key] !== false
const recommendations = computed(() => {
  const list = Array.isArray(props.recommendations) ? props.recommendations : []
  return list.slice(0, 4)
})
function resolveRecommendHref(href: string) {
  const s = String(href || '').trim()
  if (!s) return ''
  if (s.startsWith('#')) return s
  if (/^https?:\/\//.test(s)) return s
  const p = s.startsWith('/') ? s : `/${s}`
  if (runtimeBasePath() !== '/') return `${siteUrl}${p}`
  return p
}

const defaultLandingSeo = {
  h1: props.breadcrumbName || '视频转高清',
  howto: [],
  whereTitle: '',
  whereItems: [],
  featuresTitle: '',
  featuresItems: [],
  whyNeedTitle: '',
  whyNeedItems: [],
  whyTitle: '',
  whyItems: [],
  quoteTitle: '',
  quoteItems: [],
  faqTitle: '常见问题 (FAQ)',
  faq: [],
}
const landingSeo = computed(() => (props.landingSeo ? props.landingSeo : defaultLandingSeo))

const canonicalPath = computed(() => {
  const p = String(props.canonicalPath || '').trim() || '/video-enhance-app/'
  const v = p.startsWith('/') ? p : `/${p}`
  return v.endsWith('/') ? v : `${v}/`
})
const canonical = computed(() => `${siteUrl}${canonicalPath.value}`)

const htmlLang = computed(() => {
  const m = props.hreflang || {}
  const k = Object.keys(m).find((x) => x && x !== 'x-default') || ''
  return k || 'zh-CN'
})

const isPrimaryLanding = computed(() => {
  if (props.embed) return false
  const id = String(props.landingPageId || '').trim()
  return id === 'video-enhance'
})

const orgJsonLd = computed(() => {
  return organizationJsonLd(siteUrl, absoluteUrl(siteUrl, brand.logoPath))
})

const webAppJsonLd = computed(() => {
  if (!isPrimaryLanding.value) return ''
  return webApplicationJsonLd({
    siteUrl,
    name: '无痕AI视频增强',
    description: `${brand.productName}是一款智能图片及视频批处理平台，支持视频转高清、视频去字幕、视频去水印等能力。`,
    inLanguage: htmlLang.value,
  })
})

const webPageJsonLd = computed(() => {
  const out: any = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical.value}#webpage`,
    url: canonical.value,
    name: pageTitle.value,
    description: description.value,
    inLanguage: htmlLang.value,
    isPartOf: { '@id': `${siteUrl}#website` },
  }
  if (isPrimaryLanding.value) out.about = { '@id': `${siteUrl}#webapp` }
  return JSON.stringify(out)
})

const breadcrumbJsonLd = computed(() => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: brand.productName, item: siteUrl },
      { '@type': 'ListItem', position: 2, name: props.breadcrumbName || '视频转高清', item: canonical.value },
    ],
  })
})
const pageTitle = computed(() => {
  const t = String(props.seoTitle || '').trim()
  if (t) return t
  return landingSeo.value.h1 ? `${landingSeo.value.h1} - ${brand.productName}` : brand.productName
})
const description = computed(() => String(props.seoDescription || '').trim())

useHead(() => {
  const link: any[] = [{ rel: 'canonical', href: canonical.value }]
  const h = props.hreflang || {}
  for (const [lang, href] of Object.entries(h)) {
    const u = String(href || '').trim()
    if (!u) continue
    link.push({ rel: 'alternate', hreflang: lang, href: u.startsWith('http') ? u : `${siteUrl}${u}` })
  }
  const scripts: any[] = []
  if (isPrimaryLanding.value) {
    scripts.push({ type: 'application/ld+json', textContent: orgJsonLd.value })
    scripts.push({ type: 'application/ld+json', textContent: webAppJsonLd.value })
  }
  scripts.push({ type: 'application/ld+json', textContent: webPageJsonLd.value })
  scripts.push({ type: 'application/ld+json', textContent: breadcrumbJsonLd.value })
  return {
    htmlAttrs: { lang: htmlLang.value },
    title: pageTitle.value,
    meta: description.value ? [{ name: 'description', content: description.value }] : [],
    link,
    script: scripts,
  }
})

function whyNeedImageSrc(idx: number) {
  const list = Array.isArray(props.benefitsImages) ? props.benefitsImages : []
  const i = Math.max(0, Math.floor(Number(idx || 0)))
  const pick = list.length ? String(list[i] || list[i % list.length] || '').trim() : ''
  if (pick) return withBase(pick)
  const j = (Math.abs(Number(idx || 0)) % 2) + 1
  return withBase(`/assets/why-need-${j}.jpg`)
}

const featureItems = computed(() => {
  const list = Array.isArray(props.featuresImages) ? props.featuresImages : []
  const items = Array.isArray((landingSeo.value as any)?.featuresItems) ? ((landingSeo.value as any).featuresItems as any[]) : []
  return items.map((f: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return f
    return { ...f, image: img }
  })
})

const whyChooseItems = computed(() => {
  const list = Array.isArray(props.whyChooseImages) ? props.whyChooseImages : []
  const items = Array.isArray((landingSeo.value as any)?.whyItems) ? ((landingSeo.value as any).whyItems as any[]) : []
  return items.map((w: any, idx: number) => {
    const img = list.length ? String(list[idx] || list[idx % list.length] || '').trim() : ''
    if (!img) return w
    return { ...w, image: img }
  })
})

const videoPanelEl = ref<HTMLElement | null>(null)
const videoEl = ref<HTMLVideoElement | null>(null)
const videoFileInput = ref<HTMLInputElement | null>(null)
const videoFile = ref<File | null>(null)
const videoUrl = ref('')
const demoPlayed = ref(false)
const isMobileViewport = ref(false)
const videoW = ref(0)
const videoH = ref(0)
const videoDuration = ref(0)
const originObjectKey = ref('')
const uploadBusy = ref(false)
const initDone = ref(false)
const taskPanelHeight = ref(0)

const upscaleOption = ref<UpscaleOption>('UPS_1080P')
const upscaleOptions = [
  { value: 'UPS_1080P', label: '1080P高清' },
  { value: 'UPS_2K', label: '2K超清' },
] as const

const warnOpen = ref(false)
const warnText = ref('')
function openWarn(msg: string) {
  warnText.value = String(msg || '').trim()
  warnOpen.value = true
}
function closeWarn() {
  warnOpen.value = false
  warnText.value = ''
}

const submitBusy = ref(false)
const downloadingTaskId = ref('')
const deletingTaskId = ref('')
const cancelingTaskId = ref('')

type SubmitStep = 'upload' | 'create' | 'start'
type SubmitStage = 'uploading' | 'creating' | 'starting' | 'error'
const submitOpen = ref(false)
const submitStage = ref<SubmitStage>('uploading')
const submitUploadPct = ref(0)
const submitErrorText = ref('')
const submitErrorStep = ref<SubmitStep>('upload')
function openSubmitDialog() {
  submitOpen.value = true
  submitStage.value = 'uploading'
  submitUploadPct.value = 0
  submitErrorText.value = ''
  submitErrorStep.value = 'upload'
}
function closeSubmitDialog() {
  submitOpen.value = false
}
function setSubmitError(step: SubmitStep, message: string) {
  submitStage.value = 'error'
  submitErrorStep.value = step
  submitErrorText.value = message
}

function handleAuthExpiredDuringSubmit() {
  closeSubmitDialog()
  try {
    auth.clearToken()
  } catch {}
  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })
}
function onAuthExpired() {
  handleAuthExpiredDuringSubmit()
}

type RechargeEntry = 'manual' | 'insufficient'
async function openRechargeModal(entry: RechargeEntry, creditsRequired: number, tipText?: string) {
  if (!ensureLoggedIn()) return
  try {
    await paymentModal.pay({ entry, creditsRequired, tipText })
  } catch {}
}

function ensureLoggedIn(): boolean {
  if (auth.token) return true
  loginModal.show({ tab: 'sms', defaultInvitationCode: props.defaultInvitationCode })
  return false
}

function revokeVideoUrl() {
  if (videoUrl.value && videoUrl.value.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(videoUrl.value)
    } catch {}
  }
  videoUrl.value = ''
}
function resetVideo() {
  revokeVideoUrl()
  videoFile.value = null
  videoW.value = 0
  videoH.value = 0
  videoDuration.value = 0
  originObjectKey.value = ''
}

const isDemoVideo = computed(() => !videoFile.value && !!videoUrl.value && videoUrl.value === String(props.demoVideoUrl || '').trim())
function syncMobileViewport() {
  const w = Number(window.innerWidth || 0) || 0
  isMobileViewport.value = w > 0 && w < 640
}

let taskPollTimer = 0
let taskPollBusy = false
let videoPanelRO: ResizeObserver | null = null
function stopTaskPolling() {
  if (taskPollTimer) {
    try {
      window.clearInterval(taskPollTimer)
    } catch {}
  }
  taskPollTimer = 0
  taskPollBusy = false
  try {
    taskStore.stopPolling()
  } catch {}
}

function hasActiveTasksInList() {
  return videoTasks.items
    .filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
    .some((t: any) => {
      const s = normalizeTaskStatus(t?.status)
      return s === TASK_STATUS.queued || s === TASK_STATUS.processing
    })
}

async function pollTaskDetailsOnce() {
  if (taskPollBusy) return
  if (!auth.token) {
    stopTaskPolling()
    return
  }
  const refreshStatusOnly = async (token: string, taskId: string) => {
    const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token })
    const data = assertOk(resp, '任务查询失败') as any
    const p = Number((data as any)?.progress)
    const progress = Number.isFinite(p) ? Math.max(0, Math.round(p)) : undefined
    videoTasks.upsert({
      task_id: String((data as any)?.task_id || taskId).trim() || taskId,
      status: normalizeTaskStatus((data as any)?.status || 'processing') as any,
      progress,
      metering: (data as any)?.metering,
      preview_url: (data as any)?.preview_url || undefined,
      download_url: (data as any)?.download_url || undefined,
      description: (data as any)?.description || undefined,
      upscale_option: (data as any)?.upscale_option || undefined,
      update_time: Date.now(),
      task_name: String((data as any)?.task_name || '').trim() || undefined,
    } as any)
  }
  const activeIds = videoTasks.items
    .filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
    .filter((t: any) => {
      const s = normalizeTaskStatus(t?.status)
      return s === TASK_STATUS.queued || s === TASK_STATUS.processing
    })
    .map((t: any) => String(t?.task_id || '').trim())
    .filter(Boolean)
  if (!activeIds.length) {
    stopTaskPolling()
    return
  }
  taskPollBusy = true
  try {
    const token = String(auth.token || '').trim()
    if (!token) return
    await Promise.allSettled(activeIds.map((id) => refreshStatusOnly(token, id)))
  } finally {
    taskPollBusy = false
  }
  if (!hasActiveTasksInList()) stopTaskPolling()
}

function startTaskPollingIfNeeded() {
  if (taskPollTimer) return
  if (!hasActiveTasksInList()) return
  taskPollTimer = window.setInterval(() => {
    void pollTaskDetailsOnce()
  }, 15000)
  void pollTaskDetailsOnce()
}

function validateVideoMeta(): '' | 'missing_meta' | 'too_long' | 'too_large_res' {
  if (!videoW.value || !videoH.value) return 'missing_meta'
  if (videoDuration.value > 60) return 'too_long'
  const maxSide = Math.max(videoW.value, videoH.value)
  const minSide = Math.min(videoW.value, videoH.value)
  if (maxSide > 1968 || minSide > 1088) return 'too_large_res'
  return ''
}

async function handleFileSelected(file: File) {
  if (!file.type.startsWith('video/')) {
    openWarn('不支持的文件类型，请选择视频文件。')
    return
  }
  taskStore.setCurrent(null)
  originObjectKey.value = ''
  videoFile.value = file
  revokeVideoUrl()
  videoUrl.value = URL.createObjectURL(file)
}

async function playDemoVideo() {
  const demo = String(props.demoVideoUrl || '').trim()
  if (!demo) return
  if (demoPlayed.value) return
  if (videoFile.value) return
  if (videoUrl.value) return
  demoPlayed.value = true
  videoUrl.value = demo
  await nextTick()
  const v = videoEl.value
  if (!v) return
  try {
    v.muted = true
    await v.play()
  } catch {}
}

function onVideoEnded() {
  if (!isDemoVideo.value) return
  const v = videoEl.value
  if (!v) return
  if (!isMobileViewport.value) {
    try {
      v.pause()
      v.currentTime = 0
    } catch {}
    videoUrl.value = ''
    return
  }
  try {
    v.currentTime = 0
    void v.play()
  } catch {}
}

function openWeChatMiniProgram() {
  const url = 'weixin://dl/business/?appid=wx34b5be765618750e&path=pages/index/index&env_version=release'
  try {
    window.location.href = url
  } catch {}
}

function onDropzoneDragOver(e: DragEvent) {
  e.preventDefault()
  try {
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  } catch {}
}

function onDropzoneDrop(e: DragEvent) {
  e.preventDefault()
  const f = e.dataTransfer?.files?.[0]
  if (f) void handleFileSelected(f)
}

function pickVideo() {
  if (uploadBusy.value) return
  videoFileInput.value?.click()
}

async function onVideoLoadedMeta() {
  const vid = videoEl.value
  if (!vid) return
  videoW.value = Math.round(vid.videoWidth || 0)
  videoH.value = Math.round(vid.videoHeight || 0)
  videoDuration.value = Number.isFinite(vid.duration) ? vid.duration : 0
  await nextTick()
}

function pad2(n: number) {
  return String(Math.max(0, Math.floor(Number(n) || 0))).padStart(2, '0')
}
function formatMmssFromSeconds(v: unknown) {
  const n = Math.floor(Number(v))
  if (!Number.isFinite(n) || n < 0) return '00:00'
  const mm = Math.floor(n / 60)
  const ss = n % 60
  const m = String(mm)
  return `${m.length < 2 ? m.padStart(2, '0') : m}:${pad2(ss)}`
}
function formatMdHm(ms: unknown) {
  const n = Math.floor(Number(ms))
  if (!Number.isFinite(n) || n <= 0) return '--'
  const d = new Date(n)
  const mm = pad2(d.getMonth() + 1)
  const dd = pad2(d.getDate())
  const hh = pad2(d.getHours())
  const mi = pad2(d.getMinutes())
  return `${mm}-${dd} ${hh}:${mi}`
}

function getTaskUpscaleOption(t: any): UpscaleOption {
  const v = String(t?.upscale_option || t?.upscaleOption || '').trim()
  if (v === 'UPS_2K') return 'UPS_2K'
  return 'UPS_1080P'
}

function estimateUpscaleMinutes(t: any) {
  const sec = Math.max(0, Math.floor(Number(t?.metering || 0)) || 0)
  if (!sec) return 0
  const opt = getTaskUpscaleOption(t)
  const unit = Math.max(1, Math.ceil(sec / 15))
  const perUnit = opt === 'UPS_2K' ? 9 : 6
  return unit * perUnit
}

function queueHintText(t: any) {
  const p = Math.max(0, Math.round(Number(t?.progress || 0)) || 0)
  if (p <= 0) return '即将开始处理'
  return `前面还有${p}个任务在处理`
}

function processingHintText(t: any) {
  const p = Math.max(0, Math.round(Number(t?.progress || 0)) || 0)
  if (p > 0) return `处理进度：${p}%`
  const m = estimateUpscaleMinutes(t)
  if (m > 0) return `预计${m}分钟内完成`
  return '正在处理'
}

const canSubmit = computed(() => !!videoFile.value && !uploadBusy.value)
const refreshDisabled = computed(() => !auth.token || uploadBusy.value || videoTasks.refreshing || videoTasks.listing)
const visibleTasks = computed(() => {
  if (!initDone.value) return []
  if (!auth.token) return []
  return videoTasks.items.filter((x: any) => x.task_type === TASK_TYPE && Math.max(0, Number(x?.sub_task_len || 0)) === 0)
})
const showPager = computed(() => videoTasks.listTotalPages > 1)
const pageText = computed(() => `${videoTasks.listPageIndex + 1}/${videoTasks.listTotalPages}`)
const canPrevPage = computed(() => videoTasks.listPageIndex > 0)
const canNextPage = computed(() => videoTasks.listPageIndex + 1 < videoTasks.listTotalPages)
const submitPriceText = computed(() => getVideoUpscaleCreditsStandardText(upscaleOption.value))
const upscaleHintText = computed(() => getVideoUpscaleHintText(upscaleOption.value))

function statusKey(st: unknown) {
  return normalizeTaskStatus(st)
}

async function refreshTasks() {
  if (!auth.token) return
  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, videoTasks.listPageIndex, 's', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  startTaskPollingIfNeeded()
}
async function gotoPage(pageIndex: number) {
  if (!auth.token) return
  stopTaskPolling()
  try {
    await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, pageIndex, 's', undefined, true, { authExpiredMode: 'silent' })
  } catch (e) {
    if (isAuthRequiredError(e)) {
      videoTasks.reset()
      return
    }
  }
  startTaskPollingIfNeeded()
}
function prevPage() {
  if (!canPrevPage.value) return
  void gotoPage(videoTasks.listPageIndex - 1)
}
function nextPage() {
  if (!canNextPage.value) return
  void gotoPage(videoTasks.listPageIndex + 1)
}

async function startExistingTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  if (uploadBusy.value) return
  openSubmitDialog()
  submitStage.value = 'starting'
  submitUploadPct.value = 100
  uploadBusy.value = true
  try {
    const startResp = await apiRequest<StartTaskResponse>('/task/start', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    const startCode = Number((startResp as any).code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof (startResp as any).data?.credits_required === 'number' ? (startResp as any).data.credits_required : 0
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String((startResp as any).message || '').trim())
      return
    }
    if (startCode !== 0) throw new Error(String((startResp as any).message || '任务启动失败，请重试'))
    {
      const st = statusKey((startResp as any).data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    closeSubmitDialog()
    openWarn(e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
  }
}

async function cancelTask(taskId: string) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  if (uploadBusy.value) return
  if (cancelingTaskId.value) return
  cancelingTaskId.value = taskId
  try {
    const resp = await apiRequest<any>('/task/stop', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '取消失败'))
    {
      const st = statusKey((resp as any)?.data?.status) || TASK_STATUS.paused
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }
  } catch (e) {
    openWarn(e instanceof Error ? e.message : String(e))
  } finally {
    cancelingTaskId.value = ''
  }
}

async function removeTask(taskId: string) {
  if (deletingTaskId.value) return
  if (!auth.token) {
    removeTaskThumbCache(taskId)
    videoTasks.remove(taskId)
    return
  }
  if (!ensureLoggedIn()) return
  deletingTaskId.value = taskId
  try {
    const resp = await apiRequest<any>('/task/delete', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ task_id: taskId }),
    } as any)
    if (Number(resp.code) !== 0) throw new Error(String(resp.message || '删除失败'))
    removeTaskThumbCache(taskId)
    videoTasks.remove(taskId)
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, videoTasks.listPageIndex, 's', undefined, true, { authExpiredMode: 'silent' })
      if (videoTasks.listPageIndex >= videoTasks.listTotalPages) {
        await videoTasks.fetchRecent(
          auth.token,
          1,
          TASK_TYPE,
          Math.max(0, videoTasks.listTotalPages - 1),
          's',
          undefined,
          true,
          { authExpiredMode: 'silent' },
        )
      }
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }
  } catch (e) {
    openWarn(e instanceof Error ? e.message : String(e))
  } finally {
    deletingTaskId.value = ''
  }
}

async function downloadTask(t: any) {
  if (!ensureLoggedIn()) return
  if (!auth.token) return
  if (downloadingTaskId.value) return
  const taskId = String(t?.task_id || '').trim()
  if (!taskId) return
  downloadingTaskId.value = taskId
  try {
    let url = String(t?.download_url || '').trim()
    if (!url) {
      const resp = await apiRequest<any>(`/task/${encodeURIComponent(taskId)}`, { method: 'GET', token: auth.token })
      const data = assertOk(resp, '任务下载存在异常，请联系客服解决。') as any
      url = String(data?.download_url || '').trim()
      if (!url) throw new Error('missing_download_url')
      videoTasks.upsert({ task_id: taskId, download_url: url } as any)
    }
    const rawName = String(t?.task_name || t?.title || '').trim() || '视频转高清'
    const safeName = rawName.replace(/[\\/:*?"<>|]+/g, '_').slice(0, 80).trim() || '视频转高清'
    let ext = '.mp4'
    try {
      const p = new URL(url).pathname
      const m = p.match(/\.([A-Za-z0-9]{2,6})$/)
      if (m?.[1]) ext = `.${m[1]}`
    } catch {}
    const fileName = safeName.toLowerCase().endsWith(ext.toLowerCase()) ? safeName : `${safeName}${ext}`
    const resp = await fetch(url)
    if (!resp.ok) throw new Error('download_failed')
    const blob = await resp.blob()
    const blobUrl = URL.createObjectURL(blob)
    try {
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileName
      a.rel = 'noopener'
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } finally {
      try {
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500)
      } catch {}
    }
  } catch (e) {
    if (isAuthRequiredError(e)) return
    openWarn('任务下载存在异常，请联系客服解决。')
  } finally {
    downloadingTaskId.value = ''
  }
}

async function submit() {
  if (!ensureLoggedIn()) return
  if (!videoFile.value) return
  if (uploadBusy.value) return

    if (upscaleOption.value !== 'UPS_1080P' && upscaleOption.value !== 'UPS_2K') upscaleOption.value = 'UPS_1080P'

  const metaError = validateVideoMeta()
  if (metaError) {
    if (metaError === 'too_long')
      openWarn('视频转高清算力消耗较大，目前支持视频最大长度为1分钟。请将视频拆解为多段1分钟内的片段，分别提交。')
    else if (metaError === 'too_large_res') openWarn('视频分辨率超过1080P，建议压缩至1080P后再提交。')
    else openWarn('视频信息读取失败，请重新选择视频后再提交。')
    return
  }

  openSubmitDialog()
  uploadBusy.value = true
  submitBusy.value = true
  try {
    if (!originObjectKey.value) {
      let upload: any
      try {
        upload = await getUploadUrl(auth.token, videoFile.value)
      } catch (e) {
        if (isAuthRequiredError(e)) {
          return
        }
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }

      submitStage.value = 'uploading'
      submitUploadPct.value = 0
      try {
        await putFile(upload, videoFile.value, (p) => {
          submitUploadPct.value = p.percent
        })
      } catch {
        setSubmitError('upload', '视频上传失败，请重新提交。')
        return
      }
      originObjectKey.value = upload.object_key || ''
    }
    if (!originObjectKey.value) throw new Error('上传成功但缺少 object_key')
    submitUploadPct.value = 100

    submitStage.value = 'creating'
    const payload: any = { source_video: originObjectKey.value, upscale_option: upscaleOption.value }
    const rawName = String(videoFile.value.name || '').trim()
    const taskName = rawName.replace(/\.[^./\\]+$/, '')
    if (taskName) payload.task_name = taskName

    let taskId = ''
    let creditsRequired = 0
    try {
      const resp = await apiRequest<VideoUpscaleResponse>('/video/upscale', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      } as any)
      const data = assertOk(resp, '提交失败') as any
      taskId = String(data.task_id || '').trim()
      creditsRequired = typeof data.credits_required === 'number' ? data.credits_required : 0
      if (!taskId) throw new Error('empty task_id')
    } catch {
      setSubmitError('create', '任务创建失败，请重新提交或联系客服处理。')
      return
    }

    taskStore.setCurrent({ task_id: taskId, status: 'created' } as any)
    videoTasks.upsert({
      task_id: taskId,
      title: videoFile.value.name || '视频转高清',
      created_at: Date.now(),
      task_type: TASK_TYPE,
      status: 'created' as any,
      source_video: originObjectKey.value,
      upscale_option: upscaleOption.value,
    } as any)
    try {
      await videoTasks.fetchRecent(auth.token, 1, TASK_TYPE, 0, 's', undefined, true, { authExpiredMode: 'silent' })
    } catch (e) {
      if (isAuthRequiredError(e)) videoTasks.reset()
    }

    submitStage.value = 'starting'
    let startResp: any
    try {
      startResp = await apiRequest<StartTaskResponse>('/task/start', {
        method: 'POST',
        token: auth.token,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: taskId }),
      } as any)
    } catch (e) {
      if (isAuthRequiredError(e)) return
      throw e
    }
    const startCode = Number((startResp as any).code)
    if (startCode === 3001 || startCode === 3002) {
      const required = typeof (startResp as any).data?.credits_required === 'number' ? (startResp as any).data.credits_required : creditsRequired
      closeSubmitDialog()
      void openRechargeModal('insufficient', required, String((startResp as any).message || '').trim())
      return
    }
    if (startCode !== 0) {
      setSubmitError('start', '任务启动失败，请重试')
      return
    }
    {
      const st = statusKey((startResp as any).data?.status) || TASK_STATUS.queued
      videoTasks.upsert({ task_id: taskId, status: st as any } as any)
      taskStore.setCurrent({ task_id: taskId, status: st } as any)
    }

    try {
      const thumb = await createThumb()
      if (thumb) {
        setTaskThumbToCache(taskId, thumb)
        videoTasks.upsert({ task_id: taskId, update_time: Date.now() } as any)
      }
    } catch {}
    void (async () => {
      try {
        await auth.fetchMe()
      } catch {}
    })()
    resetVideo()
    void refreshTasks()
    closeSubmitDialog()
  } catch (e) {
    if (isAuthRequiredError(e)) return
    setSubmitError('create', e instanceof Error ? e.message : String(e))
  } finally {
    uploadBusy.value = false
    submitBusy.value = false
  }
}

function syncTaskPanelHeight() {
  const el = videoPanelEl.value
  if (!el) {
    taskPanelHeight.value = 0
    return
  }
  const h = Math.round(el.getBoundingClientRect().height || 0)
  if (h <= 0) return
  const viewportH = Math.round((window?.innerHeight || 0) as number) || 0
  const minH = 360
  const maxH = viewportH ? Math.max(520, Math.round(viewportH * 0.9)) : 920
  const nextH = Math.max(minH, Math.min(h, maxH))
  if (Math.abs(taskPanelHeight.value - nextH) >= 1) taskPanelHeight.value = nextH
}

function onStorage() {
  try {
    const v = localStorage.getItem(AUTH_EXPIRED_STORAGE_KEY)
    if (v) onAuthExpired()
  } catch {}
}

onMounted(async () => {
  initDone.value = false
  videoTasks.init()
  void refreshTasks()
  initDone.value = true
  void playDemoVideo()
  try {
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  } catch {}
  try {
    window.addEventListener('storage', onStorage)
  } catch {}
  try {
    syncMobileViewport()
    window.addEventListener('resize', syncMobileViewport)
  } catch {}
  try {
    taskPanelHeight.value = 0
    await nextTick()
    syncTaskPanelHeight()
    try {
      requestAnimationFrame(() => syncTaskPanelHeight())
    } catch {}
    if (typeof ResizeObserver !== 'undefined') {
      videoPanelRO = new ResizeObserver(() => syncTaskPanelHeight())
      if (videoPanelEl.value) videoPanelRO.observe(videoPanelEl.value)
    }
  } catch {}
  try {
    window.addEventListener('resize', syncTaskPanelHeight)
  } catch {}
})

onBeforeUnmount(() => {
  stopTaskPolling()
  try {
    window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as any)
  } catch {}
  try {
    window.removeEventListener('storage', onStorage)
  } catch {}
  try {
    window.removeEventListener('resize', syncMobileViewport)
  } catch {}
  try {
    window.removeEventListener('resize', syncTaskPanelHeight)
  } catch {}
  try {
    if (videoPanelRO) videoPanelRO.disconnect()
    videoPanelRO = null
  } catch {}
  revokeVideoUrl()
})

onBeforeRouteLeave(() => {
  stopTaskPolling()
})

watch(
  () => taskStore.current,
  (t) => {
    if (!auth.token) return
    if (!t?.task_id) return
    videoTasks.upsert({
      task_id: t.task_id,
      status: String((t as any).status || '').toLowerCase() as any,
      progress: typeof (t as any).progress === 'number' ? (t as any).progress : undefined,
      preview_url: (t as any).preview_url || undefined,
      download_url: (t as any).download_url || undefined,
      update_time: Date.now(),
      task_name: (t as any).task_name || undefined,
    } as any)
  },
  { deep: true },
)
</script>

<template>
  <LandingBackground :enabled="!props.embed">
    <div class="min-h-full flex flex-col">
      <HomePageHeader v-if="!props.embed" />
      <main class="flex-1">
      <div v-if="!props.embed" class="container">
        <header class="hero">
          <h1 class="heroTitle">{{ landingSeo.h1 }}</h1>
        </header>
      </div>

      <div class="mx-auto max-w-[1200px] px-3 sm:px-6 py-4 sm:py-10">
        <div class="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 items-start">
          <section ref="videoPanelEl" class="rounded-2xl bg-surface-container-low border border-outline-variant/30 overflow-hidden">
            <div class="px-5 pt-5 pb-1 hidden sm:flex items-center gap-3">
              <div class="flex items-center gap-3">
                <button
                  class="inline-flex items-center gap-1.5 h-5 text-xs font-extrabold leading-none text-on-surface-variant hover:text-on-surface hover:underline underline-offset-4"
                  type="button"
                  aria-label="使用教程"
                  @click="openHowTo"
                >
                  <span class="w-4 h-4 block shrink-0" aria-hidden="true" :style="tutorialIconMaskStyle"></span>
                  <span>教程</span>
                </button>
              </div>
              <div class="ml-auto flex items-center gap-2">
                <button
                  class="my-1.5 px-5 py-2.5 rounded-[var(--radius-btn)] bg-accent text-white font-black text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  :disabled="uploadBusy"
                  @click="pickVideo"
                >
                  打开视频
                </button>
                <input
                  ref="videoFileInput"
                  type="file"
                  accept="video/*"
                  class="hidden"
                  @change="
                    (e) => {
                      const f = (e?.target as any)?.files?.[0]
                      if (f) void handleFileSelected(f)
                      ;(e?.target as any).value = ''
                    }
                  "
                />
              </div>
            </div>
            <div class="px-5 pb-5 pt-1">
              <div
                class="mt-3 relative w-full aspect-[4/3] rounded-2xl border border-white/10 overflow-hidden"
                :style="{ background: 'linear-gradient(135deg, rgba(0, 139, 255, 0.22), rgba(91, 35, 255, 0.18))' }"
                @dragover="onDropzoneDragOver"
                @drop="onDropzoneDrop"
              >
                <video
                  v-if="videoUrl"
                  ref="videoEl"
                  class="absolute inset-0 w-full h-full block"
                  style="width: 100%; height: 100%; object-fit: contain"
                  :src="videoUrl"
                  playsinline
                  :muted="isDemoVideo"
                  :autoplay="isDemoVideo"
                  :loop="isDemoVideo && isMobileViewport"
                  :controls="!isDemoVideo"
                  :disablepictureinpicture="isDemoVideo"
                  controlslist="nodownload noplaybackrate noremoteplayback"
                  @loadedmetadata="onVideoLoadedMeta"
                  @ended="onVideoEnded"
                />
                <div v-else class="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer text-center px-6" role="button" tabindex="0" aria-label="上传视频" @click="pickVideo">
                  <button
                    class="w-full max-w-sm p-6 hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                    type="button"
                    aria-label="上传视频"
                    @click="pickVideo"
                  >
                    <svg class="w-16 h-16 text-white/90 mx-auto" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="filter: drop-shadow(0 12px 36px rgba(0, 0, 0, 0.35))">
                      <g transform="translate(0 -0.5)">
                        <path
                          d="M4.5 8.5A2.5 2.5 0 0 1 7 6h7a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 14 18H7a2.5 2.5 0 0 1-2.5-2.5v-7Z"
                          stroke="currentColor"
                          stroke-width="2.2"
                          stroke-linejoin="round"
                        />
                        <path d="M16.5 10.2 20 8v8l-3.5-2.2v-3.6Z" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" />
                        <path d="M10 9.3v5.4l5-2.7-5-2.7Z" fill="currentColor" opacity="0.95" />
                      </g>
                    </svg>
                    <div class="mt-3 grid gap-1 text-center px-4">
                      <div class="text-sm font-extrabold text-white/95 tracking-[-0.02em]">将视频拖放到此</div>
                      <div class="text-[13px] font-bold text-white/70">支持MP4和MOV格式，最大1080P，最长1分钟</div>
                    </div>
                  </button>
                </div>
              </div>

              <div v-if="isDemoVideo" class="mt-4 sm:hidden">
                <button class="mobileMiniAppCta" type="button" @click="openWeChatMiniProgram">开始视频增强</button>
              </div>

              <div class="mt-6 hidden sm:flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div class="w-full sm:max-w-[260px]">
                  <div class="mb-2 text-xs font-extrabold text-on-surface-variant">{{ upscaleHintText }}</div>
                  <SegmentedSingleSelect v-model="upscaleOption" :options="(upscaleOptions as any)" active-border active-border-color="#008BFF" />
                </div>

                <button
                  class="w-full sm:w-[180px] px-5 py-2.5 rounded-[var(--radius-btn)] bg-highlight text-ink font-black hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  :disabled="!canSubmit"
                  @click="submit"
                >
                  <div v-if="submitBusy" class="text-base leading-tight">正在提交</div>
                  <div v-else class="flex flex-col items-center leading-tight">
                    <div class="text-base">提交处理</div>
                    <div class="text-xs font-extrabold opacity-80">{{ submitPriceText }}</div>
                  </div>
                </button>
              </div>
            </div>
          </section>

          <section
            class="hidden sm:flex rounded-2xl bg-surface-container-low border border-outline-variant/30 overflow-hidden flex-col h-[420px] sm:h-[520px]"
            :style="taskPanelHeight ? { height: `${taskPanelHeight}px` } : undefined"
          >
            <div class="p-5 flex items-center justify-between gap-3">
              <div class="text-base font-extrabold text-on-surface">任务列表（近1天）</div>
              <div class="flex items-center gap-1">
                <button
                  class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                  type="button"
                  aria-label="刷新"
                  :disabled="refreshDisabled"
                  @click="refreshTasks"
                >
                  <IconRefresh class="w-5 h-5" />
                </button>
                <div v-if="showPager" class="flex items-center gap-0.5">
                  <button
                    class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                    type="button"
                    aria-label="上一页"
                    :disabled="refreshDisabled || !canPrevPage"
                    @click="prevPage"
                  >
                    <IconChevronLeft class="w-5 h-5" />
                  </button>
                  <div class="text-xs font-extrabold text-on-surface-variant tabular-nums min-w-[40px] text-center">{{ pageText }}</div>
                  <button
                    class="w-9 h-9 rounded-xl text-on-surface hover:bg-white/6 transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                    type="button"
                    aria-label="下一页"
                    :disabled="refreshDisabled || !canNextPage"
                    @click="nextPage"
                  >
                    <IconChevronRight class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div class="flex-1 overflow-y-auto px-5 pb-5">
              <div v-if="!auth.token" class="rounded-2xl bg-white/4 border border-white/10 px-4 py-4 text-sm text-on-surface-variant font-extrabold">
                登录后可查看任务列表
              </div>
              <div v-else-if="!visibleTasks.length" class="rounded-2xl bg-white/4 border border-white/10 px-4 py-4 text-sm text-on-surface-variant font-extrabold">
                近1天内没有提交任务
              </div>
              <div v-else class="flex flex-col gap-3">
                <div v-for="t in visibleTasks" :key="t.task_id" class="p-4 rounded-xl border border-outline-variant/30 bg-surface-container-high">
                  <div class="flex items-start gap-3">
                    <div class="w-[54px] h-[54px] rounded-[12px] bg-white/8 border border-white/10 overflow-hidden flex-none">
                      <img v-if="taskThumbSrc(t)" :src="taskThumbSrc(t)" class="w-full h-full object-cover block" alt="" />
                        <img v-else :src="defaultVideoThumbDataUrl()" class="w-full h-full object-cover block opacity-90" alt="" />
                    </div>

                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-3">
                        <div class="text-base font-extrabold text-on-surface truncate">{{ t.title || t.task_name || '视频转高清' }}</div>
                        <div class="flex-none">
                          <TaskStatusTag :status="t.status" />
                        </div>
                      </div>
                      <div class="mt-1 flex items-center gap-0 text-xs text-on-surface-variant justify-start text-left min-w-0 overflow-hidden">
                        <div class="tabular-nums flex-none pr-1">{{ formatMmssFromSeconds(t.metering) }}</div>
                        <div class="flex-none px-1">｜</div>
                        <div class="min-w-0 truncate flex-[0_1_auto]">{{ t.description || '-' }}</div>
                        <div class="flex-none px-1">｜</div>
                        <div class="tabular-nums flex-none">{{ formatMdHm(t.created_at) }}</div>
                      </div>

                      <div class="mt-3 flex items-center gap-2">
                        <button
                          v-if="statusKey(t.status) === TASK_STATUS.queued"
                          class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                          type="button"
                          aria-label="取消"
                          :disabled="uploadBusy || cancelingTaskId === t.task_id"
                          @click="cancelTask(t.task_id)"
                        >
                          <IconClose class="w-[18px] h-[18px]" />
                        </button>
                        <div v-if="statusKey(t.status) === TASK_STATUS.queued" class="flex-1 text-right text-xs font-extrabold text-on-surface-variant">
                          {{ queueHintText(t) }}
                        </div>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.created || statusKey(t.status) === TASK_STATUS.paused || statusKey(t.status) === TASK_STATUS.failed">
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="启动"
                            :disabled="uploadBusy"
                            @click="startExistingTask(t.task_id)"
                          >
                            <IconPlay class="w-[18px] h-[18px]" />
                          </button>
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="删除"
                            :disabled="uploadBusy || deletingTaskId === t.task_id"
                            @click="removeTask(t.task_id)"
                          >
                            <IconTrash class="w-[18px] h-[18px]" />
                          </button>
                        </template>
                        <template v-else-if="statusKey(t.status) === TASK_STATUS.success">
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="下载"
                            :disabled="uploadBusy || downloadingTaskId === t.task_id"
                            @click="downloadTask(t)"
                          >
                            <IconDownload class="w-[18px] h-[18px]" />
                          </button>
                          <button
                            class="w-[30px] h-[30px] rounded-[10px] border border-outline-variant/30 bg-surface-container-high hover:bg-surface-container-highest transition-colors grid place-items-center disabled:opacity-50 disabled:cursor-default"
                            type="button"
                            aria-label="删除"
                            :disabled="uploadBusy || deletingTaskId === t.task_id"
                            @click="removeTask(t.task_id)"
                          >
                            <IconTrash class="w-[18px] h-[18px]" />
                          </button>
                        </template>
                        <div v-else-if="statusKey(t.status) === TASK_STATUS.processing" class="flex-1 text-right text-xs font-extrabold text-on-surface-variant">
                          {{ processingHintText(t) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <template v-if="!props.embed">
        <section v-if="recommendations.length" class="section" id="recommend">
          <div class="container">
            <div class="sectionCard">
              <div class="recommendGrid" aria-label="产品推荐">
                <div class="recommendCell" v-for="(r, idx) in recommendations" :key="idx">
                  <div class="recommendIcon" aria-hidden="true">
                    <img class="recommendIconImg" :src="withBase(r.icon)" alt="" loading="lazy" />
                  </div>
                  <div class="recommendBody">
                    <div class="recommendName">{{ r.name }}</div>
                    <div class="recommendDesc">{{ r.desc }}</div>
                    <a class="ghost mini recommendCta" :href="resolveRecommendHref(r.href)">
                      <span>去使用</span>
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('howTo') && landingSeo.howto?.length" class="section" id="howto">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ props.howToTitle || '如何提升视频清晰度？只需 3 步' }}</h2>
                <div class="howtoGrid" aria-label="使用步骤">
                  <div class="howtoCell" v-for="(s, idx) in landingSeo.howto" :key="idx">
                    <div class="howtoIcon" aria-hidden="true">
                      <svg viewBox="0 0 44 44" fill="none">
                        <circle cx="22" cy="22" r="20" stroke="currentColor" stroke-width="2" opacity="0.22" />
                        <circle cx="22" cy="22" r="18" stroke="currentColor" stroke-width="2" opacity="0.10" />
                        <text x="22" y="28" text-anchor="middle" font-size="18" font-weight="800" fill="currentColor">{{ Number(idx) + 1 }}</text>
                      </svg>
                    </div>
                    <div class="howtoName">{{ s.title }}</div>
                    <div class="howtoDesc">{{ s.desc }}</div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('benefits') && landingSeo.whyNeedTitle && landingSeo.whyNeedItems?.length" class="section" id="benefits">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.whyNeedTitle }}</h2>
                <div class="whyNeedGrid">
                  <div class="whyNeedCard" v-for="(x, idx) in landingSeo.whyNeedItems" :key="idx">
                    <img class="whyNeedImg" :src="whyNeedImageSrc(Number(idx))" :alt="x.title" loading="lazy" />
                    <div class="whyNeedOverlay">
                      <h3>{{ x.title }}</h3>
                      <p>{{ x.desc }}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('whyChoose') && landingSeo.whyTitle && whyChooseItems.length" class="section" id="why-choose">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.whyTitle }}</h2>
                <div class="whyChooseGrid" aria-label="为什么选择">
                  <div class="whyChooseCard" v-for="(w, idx) in whyChooseItems" :key="idx">
                    <img class="whyChooseImg" :src="withBase(w.image)" :alt="w.title" loading="lazy" />
                    <h3>{{ w.title }}</h3>
                    <p>{{ w.desc }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('features') && landingSeo.featuresTitle && featureItems.length" class="section" id="features">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.featuresTitle }}</h2>
                <div class="featuresList" aria-label="功能亮点">
                  <div class="featureRow" v-for="(f, idx) in featureItems" :key="idx">
                    <div class="featureMedia" aria-hidden="true">
                      <img class="featureImg" :src="withBase(f.image)" :alt="f.title" loading="lazy" />
                    </div>
                    <div class="featureBody">
                      <h3>{{ f.title }}</h3>
                      <p>{{ f.desc }}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('testimonials') && landingSeo.quoteTitle && landingSeo.quoteItems?.length" class="section" id="quotes">
          <div class="container">
            <div class="sectionCard">
              <article class="seo">
                <h2>{{ landingSeo.quoteTitle }}</h2>
                <div class="letterGrid" aria-label="用户推荐信">
                  <article class="letterCard" v-for="(q, idx) in landingSeo.quoteItems.slice(0, 4)" :key="idx">
                    <h3 class="letterTitle">{{ q.title }}</h3>
                    <div class="letterStars" aria-hidden="true">★★★★★</div>
                    <p class="letterText">{{ q.text }}</p>
                    <footer class="letterPerson" aria-label="推荐人信息">
                      <img class="letterAvatar" :src="withBase(`/assets/quote-avatar-${Number(idx) + 1}.jpg`)" :alt="`${q.name} 头像`" loading="lazy" />
                      <div>
                        <div class="letterName">{{ q.name }}</div>
                        <div class="letterJob">{{ q.job }}</div>
                      </div>
                    </footer>
                  </article>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('faq') && landingSeo.faq?.length" class="section faq" id="faq">
          <div class="container">
            <div class="sec-head">
              <h2>{{ landingSeo.faqTitle || '常见问题' }}</h2>
            </div>
            <div aria-label="常见问题">
              <details v-for="(f, idx) in landingSeo.faq" :key="idx">
                <summary>
                  <span>{{ f.question }}</span>
                  <span class="plus">+</span>
                </summary>
                <div class="a">{{ f.answer }}</div>
              </details>
            </div>
          </div>
        </section>

        <section v-if="sectionEnabled('contact')" class="section" id="contact">
          <div class="container" id="cta">
            <div class="cta-band">
              <h2>欢迎使用无痕AI{{ props.breadcrumbName }}</h2>
              <p>新用户赠送免费试用积分，有问题可随时联系客服</p>
              <a class="btn btn-primary" href="#">开始在线{{ props.breadcrumbName }}｜免费试用
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </a>
              <div class="cta-qr">
                <div class="qr-wrap" aria-label="客服微信二维码">
                  <img :src="withBase('/assets/custom-service-qr.webp')" alt="客服微信二维码" loading="lazy" />
                </div>
                <span class="qr-tag">在线时间：09:00 - 22:00</span>
              </div>
            </div>
          </div>
        </section>
      </template>
      </main>
      <HomePageFooter v-if="!props.embed" />
    </div>
  </LandingBackground>

  <TaskSubmitProgressDialog
    v-model:open="submitOpen"
    :stage="submitStage"
    :upload-pct="submitUploadPct"
    :error-step="submitErrorStep"
    :error-text="submitErrorText"
  />
  <MessageDialog v-model:open="warnOpen" title="提示" :message="warnText" primary-plain @primary="closeWarn" />
</template>

<style scoped>
.mobileMiniAppCta {
  width: 100%;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #E4FF30;
  color: rgba(54, 47, 79, 0.98);
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 180ms ease, border-color 180ms ease, transform 180ms ease, filter 180ms ease;
}

.mobileMiniAppCta:hover {
  filter: brightness(0.94);
}
</style>
